import {TextInput, TextInputHidden, SquareButton, HeaderBig, LoadButton, NavigateSmart, LinkSmart} from '../common/Common';
import { useContext, useEffect, useState } from 'react';
import './Register.css';
import {checkEmail, postWithCsrf, setLoginStatesFromJson, setLoginStatesFromJsonConsumer} from '../common/util';
import { LocaleContext, LoginContext, OverlayContext, ScrollContext, StateContext } from '../common/App';
import { Link, Navigate } from 'react-router-dom';
import { LoadState, UserType } from '../common/enums';
import { maxPasswordLength, maxUsernameLength } from '../common/globals_common';
import { ConsumerContext } from '..';
import { useNavigateSmart } from '../common/hooks';

export function Register(props) {
    const [email, setEMail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [conditionsAccepted, setConditionsAccepted] = useState(false);
    const [newsletterAccepted, setNewsletterAccepted] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [loadState, setLoadState] = useState(LoadState.NONE);

    const state = useContext(StateContext);
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const consumer = useContext(ConsumerContext);
    const scroll = useContext(ScrollContext);
    const overlay = useContext(OverlayContext);

    const navigate = useNavigateSmart();

    function handleConditionsChange(e) {
        setConditionsAccepted(e.target.checked);
    }

    function checkInputForButton() {
        if(!email || !password) {
            return false;
        }

        if(email === "" || password === ""){
            return false;
        }

        if(!conditionsAccepted){
            return false;
        }

        if(password != passwordConfirmation){
            return false;
        }

        return true;
    }

    function handleSubmit(e) {
        e.preventDefault();

        if(checkInputForButton()){
            try {
                postWithCsrf(
                    "/user/register",
                    JSON.stringify({email: email, password: password, newsletter: newsletterAccepted, userType: props.userType}),
                    state
                ).then((response) => {
                    if(response.ok) {
                        return response.json();
                    }
                    else {
                        if(response.status == 521) {
                            return response.text().then((text) => {
                                overlay.setErrorPopupInfo({"title": locale.msgs.REGISTER_ERROR, "text": text});
                                overlay.setErrorPopupDisplayed(true);

                                throw new Error("Response code: " + response.status);
                            });
                        }
                        else {
                            throw new Error("Response code: " + response.status);
                        }
                    }
                }).then((json) => {
                    setLoginStatesFromJson(json, login);
                    setLoginStatesFromJsonConsumer(json, consumer);
                    setLoadState(LoadState.LOAD_SUCCESS);
                    navigate("/confirm_mail");
                }).catch((error) => {
                    setLoadState(LoadState.LOAD_ERROR);

                    console.log("Register ERROR: " + error);
                    
                    setErrorText(
                        <div className="text_error">
                            {locale.msgs.REGISTRATION_FAILED}&nbsp;
                            <LinkSmart onClick={() => scroll.scrollToPlain(0, 0)} className="text_error" to="/login">{locale.msgs.GO_TO_LOGIN}</LinkSmart>
                        </div>
                    )
                });

                // Calling setLoadState() after postWithCsrf() as it could throw an exception
                setLoadState(LoadState.LOADING);
                setErrorText("");
            }
            catch(e) {
                console.log(e);
            }
        }
    }


    // EFFECTS:

    useEffect(() => {
        setErrorText("");
    }, [email, password]);


    // RENDER:

    const pwdPattern = '^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\'!@#$%^&*()_+-=[]{}|;:,.<>?/~`"`´^°~\\])(?!^\d+$).{8,}$';
    let inputValid = checkInputForButton();

    return (
        <div className="bottom_padding" id="register" onSubmit={handleSubmit}>
            { login.loggedIn ?
                <NavigateSmart to="/dashboard" />
            :
                null
            }

            <form>
                <div id="email_box">
                    <TextInput text={locale.msgs.EMAIL} name="email" type="email" value={email} autocomplete="new-email"
                            onChange={(value) => setEMail(value)}
                            checker={(v) => checkEmail(v)}
                            errorText={locale.msgs.ENTER_VALID_EMAIL_ADDRESS}
                            maxLength={maxUsernameLength}
                    />
                </div>

                <NewPasswordInput password={password} setPassword={setPassword}
                        passwordConfirmation={passwordConfirmation} setPasswordConfirmation={setPasswordConfirmation} />

                <div className="checkbox_box" id="conditions_box">
                    <input type="checkbox" name="conditions" id="conditions_checkbox" className="input_radio_check checkbox"
                            onChange={handleConditionsChange} checked={conditionsAccepted}></input>
                    <label className="noselect checkbox_label" htmlFor="conditions_checkbox">

                        {locale.msgs.ACCEPT_TOS_1}
                        <LinkSmart className="link_2" to={"/terms_of_use"} target="_blank">{locale.msgs.TOS_2}</LinkSmart>
                        {locale.msgs.ACCEPT_TOS_2}
                        <LinkSmart className="link_2" to={"/privacy_policy_register"} target="_blank">{locale.msgs.PRIVACY_POLICY_REGISTER}</LinkSmart>
                        {locale.msgs.ACCEPT_TOS_3}

                    </label>
                </div>

                <div className="checkbox_box" id="register_newsletter_box">
                    <input type="checkbox" name="register_newsletter" id="register_newsletter_checkbox" className="input_radio_check checkbox"
                            onChange={(e) => setNewsletterAccepted(e.target.checked)} checked={newsletterAccepted}></input>
                    <label className="noselect checkbox_label" htmlFor="register_newsletter_checkbox">
                        {locale.msgs.REGISTER_NEWSLETTER_DESCR}
                    </label>
                </div>

                <div id="register_btn_box">
                    <LoadButton white={false} text={locale.msgs.REGISTER}
                            clickable={inputValid}
                            saving={loadState === LoadState.LOADING}
                            onClick={(e) => {}}
                            notSaved={false}
                            saved={false}
                            sucText={""} errText={""}
                            submit={true}
                    />
                </div>

                <div className="text_small_small_2 register_more_info">
                    {locale.msgs.MORE_INFO_IN_GENERAL_PRIVACY_POLICY}
                    <LinkSmart className="link_2" to={"/privacy_policy"} target="_blank">{locale.msgs.PRIVACY_POLICY}</LinkSmart>
                    .
                </div>
            </form>

            {errorText}
        </div>
    );
}

export const NewPasswordInput = ({password, setPassword, passwordConfirmation, setPasswordConfirmation}) => {
    const locale = useContext(LocaleContext);
    const [type, setType] = useState("password");

    const [checkTrigger, setCheckTrigger] = useState(false);
    const [checkTriggerOnlyErr, setCheckTriggerOnlyErr] = useState(false);

    return (
        <div>
            <div className="password_box">
                <TextInput type="password" text={locale.msgs.PASSWORD} id="reg-password" name="reg-password"
                        tempType={type} onTempTypeChange={setType}
                        value={password} autocomplete="new-password"
                        onChange={(value) => {
                            setPassword(value);

                            if(value) {
                                setCheckTriggerOnlyErr(!checkTriggerOnlyErr);
                            }
                            
                        }}
                        onBlur={() => {
                            setCheckTrigger(!checkTrigger);
                        }}
                        maxLength={maxPasswordLength}
                        checker={(value) => {
                            // Check for lowercase letter
                            if(!/[a-z]/.test(value)) {
                                return false;
                            }

                            // Check for uppercase letter
                            if(!/[A-Z]/.test(value)) {
                                return false;
                            }

                            // Check for digit
                            if(!/[0-9]/.test(value)) {
                                return false;
                            }

                            // Check for length
                            if(value.length < 8) {
                                return false;
                            }

                            // Check for special character
                            if(!/[^a-zA-Z0-9]/.test(value)) {
                                return false;
                            }

                            return true;
                        }}
                        errorText={ <div  dangerouslySetInnerHTML={{__html: locale.msgs.ENTER_VALID_PASSWORD }}></div>}
                />
            </div>

            <div className="password_box">
                <TextInput type="password" text={locale.msgs.REPEAT_PASSWORD} id="reg-password_confirm" name="reg-password_confirm"
                        tempType={type} onTempTypeChange={setType}
                        value={passwordConfirmation}
                        autocomplete="new-password" onChange={(value) => setPasswordConfirmation(value)}
                        maxLength={maxPasswordLength}
                        checker={(value) => {
                            return password === value || password === null;
                        }}
                        manualCheckTrigger={checkTrigger}
                        manualCheckTriggerOnlyErr={checkTriggerOnlyErr}
                        errorText={locale.msgs.PASSWORDS_DO_NOT_MATCH}
                />
            </div>
        </div>
    );
}
